/**
 * F-507-FE-03 / F-603-FE-03: PatientSessionPage
 *
 * Active session screen at /session/:id/patient.
 *
 * Primary: TTS playback indicator showing translated text of doctor's speech.
 * The translated text IS the page — everything else is peripheral.
 *
 * Secondary:
 * - Direction indicator (localized in patient language)
 * - Headset connection status icon
 * - Volume slider (large touch target)
 * - "Session ended. Thank you." on session close
 * - F-603: ConnectionStatus banner for reconnection feedback
 *
 * NO navigation, menus, buttons, settings, back button.
 * Design: Calm, large fonts (24-28px for translated text), high contrast, RTL.
 *
 * WebSocket message types consumed:
 * - SESSION_ENDED / session:ended → show ended state
 * - SESSION_REJOINED (F-603)      → confirm active session on reconnect
 * - VAD_STATE / vad:state         → update direction indicator
 * - TRANSCRIPT_SEGMENT            → update displayed translated text
 *
 * TTS audio is handled by useTTSPlayback (binary WebSocket frames).
 * The hook requires a raw WebSocket object — we access it via a ref adapter
 * exposed by PatientSessionPage's own WebSocket connection.
 *
 * IMPORTANT: The patient WebSocket connects with the session join token
 * (stored in sessionStorage after successful join), not a doctor JWT.
 * This page manages its own WebSocket lifecycle independently of WebSocketContext
 * (which is designed for doctor auth flows).
 *
 * F-603 reconnection: exponential backoff with 100ms base, 5000ms max, +/-50% jitter.
 * On reconnect, sends SESSION_REJOIN with the sessionId.
 */

import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useParams } from 'react-router-dom';
import { useHeadsetDetection } from '@/features/session/hooks/useHeadsetDetection';
import { useAudioCapture } from '@/features/session/hooks/useAudioCapture';
import { useVAD } from '@/features/session/hooks/useVAD';
import { useTTSPlayback } from '@/features/session/hooks/useTTSPlayback';
import { ConnectionStatus } from '@/components/ConnectionStatus';

// ---------------------------------------------------------------------------
// Localised strings (Arabic pilot)
// ---------------------------------------------------------------------------

const STRINGS = {
  waitingTitle: 'في انتظار الطبيب...',                          // Waiting for the doctor...
  doctorSpeaking: 'الطبيب يتحدث...',                            // Doctor is speaking...
  patientTranslating: 'جارٍ ترجمة كلامك...',                   // Your words are being translated...
  sessionEnded: 'انتهت الجلسة. شكراً لك.',                     // Session ended. Thank you.
  headsetConnected: 'السماعة متصلة',                            // Headset connected
  headsetDisconnected: 'السماعة غير متصلة',                    // Headset not connected
  volumeLabel: 'مستوى الصوت',                                   // Volume
  ttsPlaying: 'جارٍ تشغيل الصوت...',                            // Playing audio...
};

// ---------------------------------------------------------------------------
// VAD state type
// ---------------------------------------------------------------------------

type VADState = 'idle' | 'doctor_speaking' | 'patient_speaking' | 'tts_playing';

// ---------------------------------------------------------------------------
// F-603: Reconnection parameters (same as WebSocketContext)
// ---------------------------------------------------------------------------

const PATIENT_WS_BASE_DELAY_MS = 100;
const PATIENT_WS_MAX_DELAY_MS = 5_000;
const PATIENT_WS_JITTER_FACTOR = 0.5;

function patientBackoffDelay(attempt: number): number {
  const base = Math.min(PATIENT_WS_BASE_DELAY_MS * 2 ** attempt, PATIENT_WS_MAX_DELAY_MS);
  const multiplier = 1 - PATIENT_WS_JITTER_FACTOR + Math.random() * 2 * PATIENT_WS_JITTER_FACTOR;
  return Math.round(base * multiplier);
}

// ---------------------------------------------------------------------------
// SessionStorage keys
// ---------------------------------------------------------------------------

/** Token stored by JoinPage after POST /sessions/join succeeds */
const SESSION_TOKEN_KEY = 'patient_session_token';

/**
 * Flag stored by DisclaimerPage when the patient taps "I Understand".
 * This page reads the flag and sends DISCLAIMER_ACK over the patient WS
 * as soon as the connection opens — the patient WS is established here,
 * not in DisclaimerPage, so the ACK is deferred until WS is ready.
 */
const DISCLAIMER_ACK_KEY = 'patient_disclaimer_pending';

// ---------------------------------------------------------------------------
// PatientWebSocket — manages a raw WebSocket for the patient page
// ---------------------------------------------------------------------------

function getPatientWsUrl(token: string): string {
  const base = import.meta.env.VITE_API_URL as string | undefined;
  if (base) {
    const wsBase = base.replace(/^http/, 'ws');
    return `${wsBase}/ws?token=${encodeURIComponent(token)}`;
  }
  const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
  return `${protocol}//${window.location.host}/ws?token=${encodeURIComponent(token)}`;
}

// ---------------------------------------------------------------------------
// Direction indicator component
// ---------------------------------------------------------------------------

interface DirectionIndicatorProps {
  vadState: VADState;
  isPlaying: boolean;
}

function DirectionIndicator({ vadState, isPlaying }: DirectionIndicatorProps) {
  let text: string | null = null;

  if (isPlaying || vadState === 'tts_playing') {
    text = STRINGS.ttsPlaying;
  } else if (vadState === 'doctor_speaking') {
    text = STRINGS.doctorSpeaking;
  } else if (vadState === 'patient_speaking') {
    text = STRINGS.patientTranslating;
  }

  if (!text) return null;

  return (
    <p
      className="text-clinical-text-muted text-center animate-pulse"
      style={{ fontSize: '20px' }}
      aria-live="polite"
      role="status"
    >
      {text}
    </p>
  );
}

// ---------------------------------------------------------------------------
// Headset icon component
// ---------------------------------------------------------------------------

function HeadsetIcon({ connected }: { connected: boolean }) {
  return (
    <div
      className="flex items-center gap-2"
      title={connected ? STRINGS.headsetConnected : STRINGS.headsetDisconnected}
      aria-label={connected ? STRINGS.headsetConnected : STRINGS.headsetDisconnected}
    >
      <svg
        xmlns="http://www.w3.org/2000/svg"
        viewBox="0 0 24 24"
        fill="none"
        stroke={connected ? 'var(--color-teal)' : 'var(--color-text-muted)'}
        strokeWidth={2}
        strokeLinecap="round"
        strokeLinejoin="round"
        width={28}
        height={28}
        aria-hidden="true"
      >
        <path d="M3 18v-6a9 9 0 0 1 18 0v6" />
        <path d="M21 19a2 2 0 0 1-2 2h-1a2 2 0 0 1-2-2v-3a2 2 0 0 1 2-2h3zM3 19a2 2 0 0 0 2 2h1a2 2 0 0 0 2-2v-3a2 2 0 0 0-2-2H3z" />
      </svg>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

export function PatientSessionPage() {
  const { id: sessionId } = useParams<{ id: string }>();

  // Session state
  const [sessionEnded, setSessionEnded] = useState(false);
  const [translatedText, setTranslatedText] = useState<string | null>(null);
  const [vadState, setVadState] = useState<VADState>('idle');
  // Full stream mode — own-speech interim and processing state
  const [ownInterimText, setOwnInterimText] = useState<string | null>(null);
  const [ownIsProcessing, setOwnIsProcessing] = useState(false);
  const [volume, setVolume] = useState(1);

  // WebSocket managed locally (patient token, not doctor JWT).
  // We keep both a ref (for stable callbacks) and state (to trigger re-renders
  // so useTTSPlayback receives the live WebSocket object after connection).
  const wsRef = useRef<WebSocket | null>(null);
  const [ws, setWs] = useState<WebSocket | null>(null);
  const [wsReady, setWsReady] = useState(false);

  // F-603: Reconnection state
  const [isReconnecting, setIsReconnecting] = useState(false);
  const [justReconnected, setJustReconnected] = useState(false);
  const [sessionEndedWhileDisconnected, setSessionEndedWhileDisconnected] = useState(false);
  const reconnectAttemptRef = useRef(0);
  const reconnectTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const reconnectedToastTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const hasConnectedRef = useRef(false);
  const shouldReconnectRef = useRef(true);
  const initTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Headset detection
  const { headsetConnected } = useHeadsetDetection();

  // Audio capture — microphone + AudioWorklet PCM encoder
  const {
    startCapture,
    stopCapture,
    isCapturing,
    error: audioError,
    analyserRef,
  } = useAudioCapture({ sessionId: sessionId ?? '', ws });

  // VAD — energy-based voice activity detection, reports vad:start/vad:end
  const { pauseVAD, resumeVAD } = useVAD({
    ws,
    analyserNode: analyserRef.current,
  });

  // TTS playback — receives the live ws from state (not stale ref value)
  const { isPlaying } = useTTSPlayback({
    ws,
    pauseVAD,
    resumeVAD,
  });

  // Volume ref for the audio context (applied via gain node workaround since
  // AudioContext destination volume isn't directly controllable — we update
  // a CSS variable and expose it for future gain node integration)
  const volumeRef = useRef(volume);
  useEffect(() => {
    volumeRef.current = volume;
  }, [volume]);

  // -------------------------------------------------------------------------
  // Connect WebSocket using patient session token (F-603: with reconnection)
  // -------------------------------------------------------------------------

  useEffect(() => {
    // Retrieve the session token stored after POST /sessions/join
    const token =
      sessionStorage.getItem(SESSION_TOKEN_KEY) ??
      localStorage.getItem(SESSION_TOKEN_KEY) ??
      '';

    shouldReconnectRef.current = true;
    reconnectAttemptRef.current = 0;
    hasConnectedRef.current = false;

    function openSocket(): void {
      if (!shouldReconnectRef.current) return;
      // Guard: don't open a second socket if one is already connecting/open
      if (wsRef.current && (wsRef.current.readyState === WebSocket.CONNECTING || wsRef.current.readyState === WebSocket.OPEN)) {
        return;
      }

      const socket = new WebSocket(getPatientWsUrl(token));
      wsRef.current = socket;

      socket.onopen = () => {
        const wasReconnecting = hasConnectedRef.current && reconnectAttemptRef.current > 0;
        reconnectAttemptRef.current = 0;
        hasConnectedRef.current = true;
        setWsReady(true);
        setWs(socket);

        if (wasReconnecting) {
          // F-603: Successful reconnection
          setIsReconnecting(false);
          setJustReconnected(true);

          if (reconnectedToastTimerRef.current) {
            clearTimeout(reconnectedToastTimerRef.current);
          }
          reconnectedToastTimerRef.current = setTimeout(() => {
            setJustReconnected(false);
            reconnectedToastTimerRef.current = null;
          }, 3_000);

          // Send SESSION_REJOIN with sessionId
          if (sessionId) {
            setTimeout(() => {
              if (socket.readyState === WebSocket.OPEN) {
                socket.send(JSON.stringify({ type: 'SESSION_REJOIN', sessionId }));
              }
            }, 50);
          }
        } else {
          // First connection — associate this WebSocket with the session in Redis
          if (sessionId) {
            socket.send(JSON.stringify({ type: 'JOIN_SESSION', sessionId }));
          }

          // Send DISCLAIMER_ACK if pending
          const pendingAck = sessionStorage.getItem(DISCLAIMER_ACK_KEY);
          if (pendingAck === 'true') {
            sessionStorage.removeItem(DISCLAIMER_ACK_KEY);
            socket.send(JSON.stringify({ type: 'DISCLAIMER_ACK', sessionId: sessionId ?? '' }));
          }
        }
      };

      socket.onmessage = (event: MessageEvent) => {
        // Binary frames handled by useTTSPlayback via ws.addEventListener
        if (typeof event.data !== 'string') return;

        let msg: { type: string; [key: string]: unknown };
        try {
          msg = JSON.parse(event.data as string) as { type: string; [key: string]: unknown };
        } catch {
          return;
        }

        const type = msg.type as string;

        if (type === 'SESSION_ENDED' || type === 'session:ended') {
          setSessionEnded(true);
          return;
        }

        // F-603: SESSION_REJOINED — server confirms session is still active
        if (type === 'SESSION_REJOINED') {
          // Session resumed successfully — nothing extra needed beyond the toast
          return;
        }

        // F-603: SESSION_ENDED with reconnect reason — session closed while offline
        if (type === 'SESSION_ENDED' && (msg.reason === 'SESSION_NOT_ACTIVE' || msg.reason === 'SESSION_NOT_FOUND')) {
          setSessionEndedWhileDisconnected(true);
          setTimeout(() => {
            setSessionEnded(true);
          }, 2_000);
          return;
        }

        if (type === 'VAD_STATE' || type === 'vad:state') {
          const newVad = msg.state ?? msg.vadState;
          if (typeof newVad === 'string') {
            setVadState(newVad as VADState);
          }
          return;
        }

        if (type === 'TRANSCRIPT_SEGMENT' || type === 'transcript:segment') {
          const direction = msg.direction as string | undefined;
          // doctor_to_patient segments contain text meant for the patient
          if (direction === 'doctor_to_patient' || !direction) {
            const text = msg.translatedText ?? msg.text;
            if (typeof text === 'string') {
              setTranslatedText(text);
            }
          }
          // Clear own-speech interim when patient's speech is finalized
          if (direction === 'patient_to_doctor') {
            setOwnInterimText(null);
            setOwnIsProcessing(false);
          }
          return;
        }

        // Full stream: own-speech interim display (patient sees their own speech)
        if (type === 'stt:interim') {
          const dir = msg.direction as string | undefined;
          if (dir === 'patient_to_doctor') {
            setOwnInterimText(String(msg.text ?? ''));
          }
          return;
        }

        // Full stream: processing state after speech pause
        if (type === 'fullstream:pause_pending') {
          const dir = msg.direction as string | undefined;
          if (dir === 'patient_to_doctor') {
            setOwnIsProcessing(true);
          }
          return;
        }
      };

      socket.onclose = () => {
        setWsReady(false);
        setWs(null);
        wsRef.current = null;

        if (shouldReconnectRef.current) {
          // Mark as reconnecting if we had previously connected
          if (hasConnectedRef.current) {
            setIsReconnecting(true);
          }
          const delay = patientBackoffDelay(reconnectAttemptRef.current);
          reconnectAttemptRef.current += 1;
          reconnectTimerRef.current = setTimeout(() => {
            openSocket();
          }, delay);
        }
      };

      socket.onerror = () => {
        // onclose fires after onerror
      };
    }

    // Defer socket creation to next microtask tick. This prevents React
    // StrictMode's synchronous Mount→Cleanup→Re-mount cycle from creating
    // two sockets: the cleanup cancels the first timer before it fires,
    // and only the re-mount's timer actually opens a socket.
    initTimerRef.current = setTimeout(() => openSocket(), 0);

    return () => {
      shouldReconnectRef.current = false;
      if (initTimerRef.current) {
        clearTimeout(initTimerRef.current);
        initTimerRef.current = null;
      }
      if (reconnectTimerRef.current) {
        clearTimeout(reconnectTimerRef.current);
        reconnectTimerRef.current = null;
      }
      if (reconnectedToastTimerRef.current) {
        clearTimeout(reconnectedToastTimerRef.current);
        reconnectedToastTimerRef.current = null;
      }
      const currentSocket = wsRef.current;
      if (currentSocket) {
        currentSocket.close();       // close FIRST (sends proper close frame)
        currentSocket.onclose = null; // THEN null handlers (prevents reconnect)
        currentSocket.onmessage = null;
        currentSocket.onerror = null;
      }
      setWs(null);
      wsRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId]);

  // -------------------------------------------------------------------------
  // Auto-start audio capture when WebSocket is ready
  // -------------------------------------------------------------------------

  useEffect(() => {
    if (wsReady && ws && !isCapturing) {
      void startCapture();
    }
    // stopCapture is idempotent (checks refs internally) and stable
    // (useCallback with [] deps), so it's safe to call unconditionally.
    // We intentionally omit isCapturing/startCapture from deps to avoid
    // an infinite loop (start→capturing=true→re-run→stop→capturing=false→...).
    return () => stopCapture();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [wsReady, ws]);

  // -------------------------------------------------------------------------
  // Volume change handler
  // -------------------------------------------------------------------------

  const handleVolumeChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setVolume(Number(e.target.value));
    },
    [],
  );

  // -------------------------------------------------------------------------
  // Determine display text
  // -------------------------------------------------------------------------

  const displayText = useMemo(() => {
    if (sessionEnded) return null;
    return translatedText;
  }, [sessionEnded, translatedText]);

  // -------------------------------------------------------------------------
  // Render — session ended
  // -------------------------------------------------------------------------

  if (sessionEnded) {
    return (
      <div
        dir="rtl"
        lang="ar"
        className="flex flex-col items-center justify-center gap-6 w-full text-center"
        style={{ minHeight: '40vh', fontFamily: "'Source Sans 3', sans-serif" }}
      >
        {/* Tick circle */}
        <div
          className="flex items-center justify-center rounded-full"
          style={{
            width: '80px',
            height: '80px',
            background: 'rgba(13, 148, 136, 0.12)',
          }}
          aria-hidden="true"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 24 24"
            fill="none"
            stroke="var(--color-teal)"
            strokeWidth={2.5}
            strokeLinecap="round"
            strokeLinejoin="round"
            width={40}
            height={40}
          >
            <polyline points="20 6 9 17 4 12" />
          </svg>
        </div>

        <p
          className="font-heading font-semibold text-clinical-charcoal"
          style={{ fontSize: '28px', lineHeight: '1.4' }}
          role="status"
          aria-live="polite"
        >
          {STRINGS.sessionEnded}
        </p>
      </div>
    );
  }

  // -------------------------------------------------------------------------
  // Render — active session
  // -------------------------------------------------------------------------

  return (
    <div
      dir="rtl"
      lang="ar"
      className="flex flex-col gap-8 w-full"
      style={{ fontFamily: "'Source Sans 3', sans-serif" }}
    >
      {/* F-603: Connection status banner — non-blocking, appears at top */}
      <ConnectionStatus
        isReconnecting={isReconnecting}
        justReconnected={justReconnected}
        sessionEndedWhileDisconnected={sessionEndedWhileDisconnected}
      />

      {/* Audio capture error */}
      {audioError && (
        <p className="text-red-600 text-center" style={{ fontSize: '16px' }} role="alert">
          {audioError}
        </p>
      )}

      {/* Primary content: translated text — THIS IS THE PAGE */}
      <div
        className="w-full min-h-[180px] flex flex-col items-center justify-center"
        aria-live="polite"
        aria-label="النص المترجم"
      >
        {/* Own-speech interim — patient sees their own speech being recognized */}
        {ownInterimText && (
          <div className="mb-4 text-center">
            <p
              className="text-clinical-text-muted font-body"
              style={{ fontSize: '18px', lineHeight: '1.5' }}
            >
              {ownInterimText}
            </p>
            {ownIsProcessing && (
              <p
                className="text-amber-600 font-body mt-1 animate-pulse"
                style={{ fontSize: '16px' }}
              >
                {'جارٍ الترجمة...'}
              </p>
            )}
          </div>
        )}

        {displayText ? (
          <p
            className="text-clinical-charcoal text-center font-body leading-relaxed"
            style={{ fontSize: '28px', lineHeight: '1.6' }}
          >
            {displayText}
          </p>
        ) : (
          <p
            className="text-clinical-text-muted text-center"
            style={{ fontSize: '22px' }}
          >
            {STRINGS.waitingTitle}
          </p>
        )}
      </div>

      {/* Direction indicator */}
      <DirectionIndicator vadState={vadState} isPlaying={isPlaying} />

      {/* Peripheral controls — headset + volume */}
      <div
        className="flex flex-col gap-5 mt-4 pt-4 border-t border-clinical-border"
      >
        {/* Headset status */}
        <div className="flex items-center justify-between">
          <span
            className="text-clinical-text-muted"
            style={{ fontSize: '18px' }}
          >
            {headsetConnected ? STRINGS.headsetConnected : STRINGS.headsetDisconnected}
          </span>
          <HeadsetIcon connected={headsetConnected} />
        </div>

        {/* Volume slider */}
        <div className="flex flex-col gap-2">
          <label
            htmlFor="patient-volume"
            className="text-clinical-text-muted"
            style={{ fontSize: '18px' }}
          >
            {STRINGS.volumeLabel}
          </label>

          <input
            id="patient-volume"
            type="range"
            min={0}
            max={1}
            step={0.05}
            value={volume}
            onChange={handleVolumeChange}
            className="w-full cursor-pointer accent-clinical-teal"
            style={{
              height: '8px',
              minHeight: '56px',
              // Make the slider track area taller for easy touch
              touchAction: 'pan-y',
            }}
            aria-label={STRINGS.volumeLabel}
            aria-valuemin={0}
            aria-valuemax={1}
            aria-valuenow={volume}
          />
        </div>
      </div>

      {/* F-603: Connection status banner */}
      {!wsReady && !isReconnecting && !justReconnected && (
        <p
          className="text-clinical-text-muted text-center"
          style={{ fontSize: '16px' }}
          role="status"
          aria-live="polite"
        >
          {'جارٍ الاتصال...'}
        </p>
      )}
    </div>
  );
}
